import React, { useState, useEffect } from 'react';
import { Lock, Unlock, Save, Trash2 } from 'lucide-react';
import SignaturePad from './SignaturePad';

interface SignatureSettings {
  locked: boolean;
  propertyManager: string | null;
  landlord: string | null;
}

const AdminSignatures: React.FC = () => {
  const [settings, setSettings] = useState<SignatureSettings>(() => {
    const stored = localStorage.getItem('signatureSettings');
    return stored ? JSON.parse(stored) : {
      locked: false,
      propertyManager: null,
      landlord: null,
    };
  });

  const [activeSignature, setActiveSignature] = useState<'propertyManager' | 'landlord'>('propertyManager');

  const handleSaveSignature = (signature: string) => {
    if (settings.locked) {
      alert('Signatures are locked. Unlock them first to make changes.');
      return;
    }

    setSettings(prev => ({
      ...prev,
      [activeSignature]: signature,
    }));
  };

  const handleClearSignature = () => {
    if (settings.locked) {
      alert('Signatures are locked. Unlock them first to make changes.');
      return;
    }

    setSettings(prev => ({
      ...prev,
      [activeSignature]: null,
    }));
  };

  const toggleLock = () => {
    setSettings(prev => ({
      ...prev,
      locked: !prev.locked,
    }));
  };

  const saveSettings = () => {
    localStorage.setItem('signatureSettings', JSON.stringify(settings));
    alert('Signature settings saved successfully!');
  };

  useEffect(() => {
    // Save settings whenever they change
    localStorage.setItem('signatureSettings', JSON.stringify(settings));
  }, [settings]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Digital Signatures</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleLock}
            className={`p-2 rounded-md ${
              settings.locked 
                ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                : 'bg-green-100 text-green-600 hover:bg-green-200'
            }`}
            title={settings.locked ? 'Unlock signatures' : 'Lock signatures'}
          >
            {settings.locked ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
          </button>
          <button
            onClick={saveSettings}
            className="p-2 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200"
            title="Save signature settings"
          >
            <Save className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setActiveSignature('propertyManager')}
            className={`px-4 py-2 rounded-lg ${
              activeSignature === 'propertyManager'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Property Manager
          </button>
          <button
            onClick={() => setActiveSignature('landlord')}
            className={`px-4 py-2 rounded-lg ${
              activeSignature === 'landlord'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Saratoga (Pty) Ltd
          </button>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium mb-4">
            {activeSignature === 'propertyManager' ? 'Property Manager Signature' : 'Landlord Signature'}
          </h3>
          
          {settings[activeSignature] ? (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-white">
                <img 
                  src={settings[activeSignature]!} 
                  alt="Signature" 
                  className="max-h-24 object-contain"
                />
              </div>
              <button
                onClick={handleClearSignature}
                disabled={settings.locked}
                className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Signature
              </button>
            </div>
          ) : (
            <SignaturePad
              onSave={handleSaveSignature}
              width={400}
              height={150}
            />
          )}
        </div>
      </div>

      <div className="text-sm text-gray-500">
        <p>These signatures will be automatically applied to:</p>
        <ul className="list-disc list-inside mt-2">
          <li>Lease agreements</li>
          <li>Official communications</li>
          <li>Legal documents</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminSignatures;